/**
 * Centralized Logging Utility (Frontend)
 * 
 * Provides structured logging with levels, formatting, and environment-based control.
 * Replaces scattered console.log statements throughout the codebase.
 * 
 * Log Levels:
 * - DEBUG: Detailed debugging information (only in development)
 * - INFO: General informational messages
 * - WARN: Warning messages for potentially harmful situations
 * - ERROR: Error messages for failures
 * 
 * Usage:
 * import logger from '../utils/logger';
 * logger.debug('Detailed debug info', { data });
 * logger.info('General info');
 * logger.warn('Warning message');
 * logger.error('Error occurred', error);
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

// Determine log level from environment
const getLogLevel = () => {
  const env = process.env.NODE_ENV || 'development';
  const configLevel = process.env.REACT_APP_LOG_LEVEL;
  
  if (configLevel) {
    return LOG_LEVELS[configLevel.toUpperCase()] || LOG_LEVELS.INFO;
  }
  
  // Default levels by environment
  if (env === 'production') {
    return LOG_LEVELS.WARN; // Only warnings and errors in production
  } else if (env === 'test') {
    return LOG_LEVELS.ERROR; // Only errors in tests
  }
  
  return LOG_LEVELS.DEBUG; // Everything in development
};

const currentLogLevel = getLogLevel();

/**
 * Get emoji prefix for log level
 */
const getEmoji = (level) => {
  switch (level) {
    case LOG_LEVELS.DEBUG: return '🔍';
    case LOG_LEVELS.INFO: return '✅';
    case LOG_LEVELS.WARN: return '⚠️';
    case LOG_LEVELS.ERROR: return '❌';
    default: return '';
  }
};

/**
 * Log a message at the specified level
 */
const log = (level, message, context = null) => {
  if (level < currentLogLevel) {
    return; // Skip if below current log level
  }
  
  const emoji = getEmoji(level);
  const prefix = emoji ? `${emoji} ` : '';
  
  switch (level) {
    case LOG_LEVELS.DEBUG:
      if (context) {
        console.debug(prefix + message, context);
      } else {
        console.debug(prefix + message);
      }
      break;
      
    case LOG_LEVELS.INFO:
      if (context) {
        console.info(prefix + message, context);
      } else {
        console.info(prefix + message);
      }
      break;
      
    case LOG_LEVELS.WARN:
      if (context) {
        console.warn(prefix + message, context);
      } else {
        console.warn(prefix + message);
      }
      break;
      
    case LOG_LEVELS.ERROR:
      if (context) {
        console.error(prefix + message, context);
      } else {
        console.error(prefix + message);
      }
      break;
    default:
      if (context) {
        console.log(prefix + message, context);
      } else {
        console.log(prefix + message);
      }
  }
};

/**
 * Logger API
 */
const logger = {
  /**
   * Log debug message (only in development)
   * @param {string} message - Log message
   * @param {*} context - Optional context data
   */
  debug: (message, context = null) => {
    log(LOG_LEVELS.DEBUG, message, context);
  },
  
  /**
   * Log info message
   * @param {string} message - Log message
   * @param {*} context - Optional context data
   */
  info: (message, context = null) => {
    log(LOG_LEVELS.INFO, message, context);
  },
  
  /**
   * Log warning message
   * @param {string} message - Log message
   * @param {*} context - Optional context data
   */
  warn: (message, context = null) => {
    log(LOG_LEVELS.WARN, message, context);
  },
  
  /**
   * Log error message
   * @param {string} message - Log message
   * @param {Error|*} error - Error object or context data
   */
  error: (message, error = null) => {
    if (error instanceof Error) {
      log(LOG_LEVELS.ERROR, message, {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    } else {
      log(LOG_LEVELS.ERROR, message, error);
    }
  },
  
  /**
   * Create a scoped logger with a prefix
   * @param {string} scope - Scope name (e.g., 'Validation', 'API')
   * @returns {object} Scoped logger
   */
  scope: (scope) => {
    return {
      debug: (message, context) => logger.debug(`[${scope}] ${message}`, context),
      info: (message, context) => logger.info(`[${scope}] ${message}`, context),
      warn: (message, context) => logger.warn(`[${scope}] ${message}`, context),
      error: (message, error) => logger.error(`[${scope}] ${message}`, error)
    };
  },
  
  /**
   * Get current log level
   */
  getLevel: () => currentLogLevel,
  
  /**
   * Check if a log level is enabled
   */
  isLevelEnabled: (level) => level >= currentLogLevel
};

export default logger;
export { LOG_LEVELS };
